import { AxiosResponse } from 'axios';

import { api } from '../../../http';
import {
  BaseFilter,
  LogsItem,
  PageRequest,
  PageResponse,
  Shift,
  ShiftToAdd
} from '../../../types/interfaces';
import { ApiResponse } from '../interfaces';

type ShiftValidationResponse = {
  isSuccess: boolean;
  data?: Shift;
  error?: string;
  errorObject?: {
    id: number;
    date: string;
    workdayStart: string;
    workdayDuration: string;
    breakDuration: string;
    personId: number;
    departmentId: number;
    departmentName: string;
  }[];
};

interface ValidationError {
  personId: number;
  date: string;
  workdayStart: string;
  workdayDuration: string;
  breakDuration: string;
  departmentId: number;
  departmentName: string;
}

type LogsParams = PageRequest & {
  sortBy?: string;
  sortDirection?: string;
  filters?: string;
  parsedFilters?: BaseFilter[];
};

const buildSearchParams = (params: any): URLSearchParams => {
  const searchParams = new URLSearchParams();
  
  if (!params) return searchParams;
  
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      if (key !== 'parsedFilters') {
        searchParams.append(key, String(value));
      }
    }
  });
  
  return searchParams;
};

export const SchedulePersonShiftService = {
  createPersonsShifts(
    shifts: ShiftToAdd[],
    validation = false
  ): Promise<AxiosResponse<ApiResponse<ShiftToAdd[]>>> {
    return api.post<ApiResponse<ShiftToAdd[]>>('SchedulePersonShift', shifts, {
      params: { validation }
    });
  },

  validateShiftCalc(
    shifts: ShiftToAdd[]
  ): Promise<AxiosResponse<ValidationError[]>> {
    return api.post<ValidationError[]>('SchedulePersonShift', shifts, {
      params: { validation: true }
    });
  },

  deleteShiftsInRange(
    shifts: ShiftToAdd[]
  ): Promise<AxiosResponse<ApiResponse<ShiftToAdd[]>>> {
    return api.delete('SchedulePersonShift', { data: shifts });
  },

  createPersonShift(
    personId: number,
    shift: Shift
  ): Promise<AxiosResponse<ApiResponse<Shift>>> {
    return api.post<ApiResponse<Shift>>(
      `SchedulePersonShift/${personId}`,
      shift
    );
  },

  validatePersonShift(
    personId: number,
    shift: Shift
  ): Promise<AxiosResponse<ShiftValidationResponse>> {
    return api.post<ShiftValidationResponse>(
      `SchedulePersonShift/${personId}/validation`,
      shift
    );
  },

  updatePersonShift(
    shiftId: number,
    shift: Shift
  ): Promise<AxiosResponse<ApiResponse<Shift>>> {
    return api.put<ApiResponse<Shift>>(`SchedulePersonShift/${shiftId}`, shift);
  },

  getPersonShiftByDate(
    personId: number,
    departmentId: number,
    date: string
  ): Promise<AxiosResponse<ApiResponse<Shift>>> {
    return api.get<ApiResponse<Shift>>(
      `SchedulePersonShift/${personId}/${departmentId}/${date}`
    );
  },

  deletePersonShift: (
    shiftId: number
  ): Promise<AxiosResponse<ApiResponse<null>>> => {
    return api.delete(`/SchedulePersonShift/${shiftId}`);
  },

  getLogs(
    params: LogsParams
  ): Promise<AxiosResponse<ApiResponse<PageResponse<LogsItem>>>> {
    return api.get<ApiResponse<PageResponse<LogsItem>>>(
      'schedulePersonShift/log',
      {
        params: buildSearchParams(params)
      }
    );
  },

  getLogsExcel(
    params?: LogsParams
  ): Promise<AxiosResponse<Blob>> {
    return api.get('schedulePersonShift/log/export', {
      params: buildSearchParams(params),
      responseType: 'blob'
    });
  },

  getPersonsTimesheet(
    date: string,
    departmentId: number
  ): Promise<AxiosResponse<ApiResponse<any>>> {
    return api.get<ApiResponse<any>>(
      `SchedulePersonShift/department/${departmentId}/timesheet`,
      { params: { date } }
    );
  },

  getTotalTimesheet(
    date: string,
    departmentId: number,
    byWeek: boolean = false,
    includeBreakDuration: boolean = true,
  ): Promise<AxiosResponse<ApiResponse<any>>> {
    return api.get<ApiResponse<any>>(
      `SchedulePersonShift/department/${departmentId}/timesheet/total`,
      { params: { date, byWeek, includeBreakDuration } }
    );
  },
};
